import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from "@mui/material";
import ActionButtons from "../../../Components/Quetionary/InitialKickoff/MaterialTopics/ActionButtons";
import { useNavigate, useParams } from "react-router-dom";
import FileUpload from "./FileUpload";
import { get, put } from "../../../utils/api";
import { numberError, textError } from "../../../utils/validationMessage";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GovernanceLayout from "../../../Layout/GovernanceLayout";
import { fixedPercentage } from "../../../utils/common";
import {
  formatNumberInputWithCommas,
  stripNumberFormatting,
} from "../../../utils/common";

/** YYYY-MM-DD bounds for effective date: within reporting year; if that year is the current calendar year, cap at today. */
const getEffectiveDateMinMax = (reportingYear) => {
  if (!Number.isFinite(reportingYear) || reportingYear <= 0)
    return { min: undefined, max: undefined };
  const pad = (n) => String(n).padStart(2, "0");
  const min = `${reportingYear - 1}-01-01`;
  const now = new Date();
  const currentYear = now.getFullYear();
  const max =
    reportingYear === currentYear
      ? `${currentYear}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
      : `${reportingYear}-12-31`;
  return { min, max };
};

const ShareholderRights = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [moduleType, setModuleType] = useState("");
  const [votesCast, setVotesCast] = useState();
  const [totalSharesOutstanding, setTotalSharesOutstanding] = useState();
  const [percentage, setPercentage] = useState("");
  const [shareholderMeetings, setShareholderMeetings] = useState("");
  const [shareholderRights, setShareholderRights] = useState("yes");
  const [documentUrl, setDocumentUrl] = useState("");
  const [isRevised, setIsRevised] = useState(false);
  const [remark, setRemark] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [previousPolicy, setPreviousPolicy] = useState({});
  const [previousPoliciesState, setPreviousPoliciesState] = useState({});
  const [errors, setErrors] = useState({});
  const [isFirstEntry, setIsFirstEntry] = useState(false);
  const [reportingYear, setReportingYear] = useState(null);

  const hasUploadedDocument = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") {
      const cleaned = value.trim();
      if (!cleaned) return false;
      if (cleaned.toLowerCase() === "no documents available") return false;
      return true;
    }
    if (value && typeof value === "object") return Object.keys(value).length > 0;
    return Boolean(value);
  };

  const sanitizeDocumentUrl = (value) => {
    if (typeof value !== "string") return value || "";
    const cleaned = value.trim();
    if (!cleaned) return "";
    if (cleaned.toLowerCase() === "no documents available") return "";
    return cleaned;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/governance/getGovernance?id=${id}`);

        const record = response.data?.data?.[0] || {};
        const data = record?.shareholderRights || {}; // Safely access shareholderRights

        const rawModule = (
          record?.module ||
          record?._governanceDataRef?.module ||
          record?.entryType ||
          ""
        )
          .toString()
          .toLowerCase()
          .trim();
        if (rawModule.includes("annual")) setModuleType("annual");
        else if (rawModule.includes("quarterly")) setModuleType("quarterly");
        else setModuleType("");

        setShareholderMeetings(
          data.shareholderMeetings !== undefined &&
            data.shareholderMeetings !== null &&
            data.shareholderMeetings !== ""
            ? formatNumberInputWithCommas(String(data.shareholderMeetings))
            : "",
        );
        setShareholderRights(
          typeof data.hasShareholderRightsPolicy?.status === "boolean"
            ? data.hasShareholderRightsPolicy.status
              ? "yes"
              : "no"
            : "yes"
        );
        const currentDocUrl = sanitizeDocumentUrl(
          data.hasShareholderRightsPolicy?.documentUrl || ""
        );
        const currentRemark = (
          data.hasShareholderRightsPolicy?.statusremark ||
          data.hasShareholderRightsPolicy?.remark ||
          ""
        ).trim();
        const currentEffectiveDate = data.hasShareholderRightsPolicy?.effectiveDate || "";
        setDocumentUrl(currentDocUrl);
        setIsRevised(
          typeof data.hasShareholderRightsPolicy?.isRevised === "boolean"
            ? data.hasShareholderRightsPolicy.isRevised
            : false
        );
        setRemark(currentRemark || "");
        setEffectiveDate(currentEffectiveDate || "");
        setPreviousPoliciesState({
          documentUrl: currentDocUrl || "",
          remark: currentRemark || "",
          effectiveDate: currentEffectiveDate || "",
        });

        // Annual previous year policy fetch (same pattern as GPolicyForm)
        if (rawModule.includes("annual")) {
          const year =
            Number(record?._governanceDataRef?.yearToSubmit) ||
            Number(record?.yearToSubmit) ||
            Number(record?.year) ||
            new Date().getFullYear();
          setReportingYear(year);
          const companyId = record?.company;
          if (year && companyId) {
            try {
              const prevRes = await get(
                `/governance/annual/previous-policies?year=${year}&companyId=${companyId}`
              );
              const prevData = prevRes?.data?.data || {};
              setIsFirstEntry(Boolean(prevData?.isFirstEntry));
              const prevShareholder = prevData?.shareholderRights?.hasShareholderRightsPolicy || {};
              setPreviousPolicy(prevShareholder || {});
              setPreviousPoliciesState({
                documentUrl: sanitizeDocumentUrl(
                   prevShareholder?.documentUrl ||
                   currentDocUrl || ""
                ),
                remark: (
                  // prevShareholder?.statusremark ||
                  prevShareholder?.remark ||
                  currentRemark ||
                  ""
                ).trim(),
                effectiveDate:
                  prevShareholder?.effectiveDate || currentEffectiveDate || "",
              });

              // Prefer previous documentUrl only if current is empty
              const prevDocUrl = sanitizeDocumentUrl(prevShareholder?.documentUrl || "");
              if (!hasUploadedDocument(currentDocUrl) && hasUploadedDocument(prevDocUrl)) {
                setDocumentUrl(prevDocUrl);
              }

              // Prefer previous remark only if current is empty
              const prevRemark = (
                prevShareholder?.statusremark ||
                prevShareholder?.remark ||
                ""
              ).trim();
              if (!currentRemark && prevRemark) {
                setRemark(prevRemark);
              }

              // Prefer previous effectiveDate only if current is empty
              const prevEffectiveDate = prevShareholder?.effectiveDate || "";
              if (!currentEffectiveDate && prevEffectiveDate) {
                setEffectiveDate(prevEffectiveDate);
              }
            } catch (prevErr) {
              console.warn("Previous shareholder policy fetch failed:", prevErr);
              setPreviousPolicy({});
              setIsFirstEntry(false);
              setPreviousPoliciesState({
                documentUrl: currentDocUrl || "",
                remark: currentRemark || "",
                effectiveDate: currentEffectiveDate || "",
              });
            }
          }
        } else {
          setPreviousPolicy({});
          setPreviousPoliciesState({});
          setIsFirstEntry(false);
          setReportingYear(null);
        }
        setVotesCast(
          data.shareholderVotingParticipationRate?.votesCast !== undefined &&
            data.shareholderVotingParticipationRate?.votesCast !== null &&
            data.shareholderVotingParticipationRate?.votesCast !== ""
            ? formatNumberInputWithCommas(
                String(data.shareholderVotingParticipationRate?.votesCast),
              )
            : "",
        );
        setTotalSharesOutstanding(
          data.shareholderVotingParticipationRate?.totalSharesOutstanding !==
            undefined &&
            data.shareholderVotingParticipationRate?.totalSharesOutstanding !==
              null &&
            data.shareholderVotingParticipationRate?.totalSharesOutstanding !==
              ""
            ? formatNumberInputWithCommas(
                String(
                  data.shareholderVotingParticipationRate
                    ?.totalSharesOutstanding,
                ),
              )
            : "",
        );
        setPercentage(
          data.shareholderVotingParticipationRate?.performance?.replace(
            "%",
            ""
          ) || ""
        );
      } catch (error) {
        console.error("Error fetching data:", error.response || error);
      }
    };

    fetchData();
  }, []);

  const validateNumbers = (value) => {
    const raw = stripNumberFormatting(value);
    return /^\d+$/.test(raw) || raw === ""; // Allow only positive integers or empty
  };

  const handleFileChange = (file) => {
    setDocumentUrl(file);
    if (hasUploadedDocument(file)) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.documentUrl;
        return newErrors;
      });
    }
  };

  const handleShareholderRightsChange = (value) => {
    setShareholderRights(value);
    if (value === "no") {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.documentUrl;
        delete newErrors.remark;
        delete newErrors.effectiveDate;
        return newErrors;
      });
    }
  };

  const handleIsRevisedChange = (revisedValue) => {
    setIsRevised(revisedValue);

    if (revisedValue) {
      // If revised is Yes, user should provide new evidence/details.
      setDocumentUrl("");
      setRemark("");
      setEffectiveDate("");
    } else {
      // If revised is No, restore previous policy values.
      setDocumentUrl(previousPoliciesState?.documentUrl || "");
      setRemark(previousPoliciesState?.remark || "");
      setEffectiveDate(previousPoliciesState?.effectiveDate || "");
    }

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors.documentUrl;
      delete newErrors.remark;
      delete newErrors.effectiveDate;
      return newErrors;
    });
  };


  const handlePercentageChange = (e, field) => {
    const value = formatNumberInputWithCommas(e.target.value);
    const numericRaw = stripNumberFormatting(value);

    // Check if the value is a valid number
    if (!numericRaw || isNaN(numericRaw) || Number(numericRaw) < 0) {
      if (field === "trained") {
        setVotesCast("");
      } else if (field === "total") {
        setTotalSharesOutstanding("");
      }
      setPercentage("");
      return;
    }

    const parsedValue = parseFloat(numericRaw);

    if (field === "trained") {
      setVotesCast(value);
    } else if (field === "total") {
      setTotalSharesOutstanding(value);
    }

    const newVotesCast =
      field === "trained"
        ? parsedValue
        : parseFloat(stripNumberFormatting(votesCast));
    const newTotalSharesOutstanding =
      field === "total"
        ? parsedValue
        : parseFloat(stripNumberFormatting(totalSharesOutstanding));

    // Check if votesCast is greater than totalSharesOutstanding
    if (newVotesCast > newTotalSharesOutstanding) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        votesCast: "No. of Votes Cast cannot exceed total.",
      }));
      setPercentage(""); // Reset percentage
    } else {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.votesCast;
        delete newErrors.totalSharesOutstanding; 
        return newErrors;
      });

      // Calculate percentage only when values are valid
      if (newTotalSharesOutstanding > 0 && newVotesCast >= 0) {
        const calculatedPercentage = 
          (newVotesCast / newTotalSharesOutstanding) *
          100
        ;
        setPercentage(fixedPercentage(calculatedPercentage));
      } else {
        setPercentage("");
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (moduleType === "annual") {
      const { min: effMin, max: effMax } = getEffectiveDateMinMax(reportingYear);
      const showIsRevisedBlock =
        !isFirstEntry &&
        hasUploadedDocument(previousPoliciesState?.documentUrl);

      const addAnnualYesMandatoryErrors = () => {
        if (!hasUploadedDocument(documentUrl)) {
          newErrors.documentUrl = "File upload is mandatory.";
        }
        if (!effectiveDate) {
          newErrors.effectiveDate = "Effective date is mandatory.";
        }
      };

      if (shareholderRights === "yes") {
        if (!showIsRevisedBlock) {
          addAnnualYesMandatoryErrors();
        } else if (isRevised) {
          addAnnualYesMandatoryErrors();
        }
      }
    } else {
      if (shareholderMeetings === "") {
        newErrors.shareholderMeetings = textError;
      } else if (!validateNumbers(shareholderMeetings)) {
        newErrors.shareholderMeetings = numberError;
      }

      if (votesCast === "") {
        newErrors.votesCast = textError;
      } else if (!validateNumbers(votesCast)) {
        newErrors.votesCast = numberError;
      }

      if (totalSharesOutstanding === "") {
        newErrors.totalSharesOutstanding = textError;
      } else if (!validateNumbers(totalSharesOutstanding)) {
        newErrors.totalSharesOutstanding = numberError;
      }

      if (parseFloat(stripNumberFormatting(totalSharesOutstanding)) < 0) {
        newErrors.totalSharesOutstanding = numberError;
      }

      // Add validation to ensure totalSharesOutstanding > votesCast
      if (
        parseFloat(stripNumberFormatting(totalSharesOutstanding)) <
        parseFloat(stripNumberFormatting(votesCast))
      ) {
        newErrors.totalSharesOutstanding =
          "Total Number Shares Outstanding must be greater than No. of Votes Cast";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const annualData = {
      hasShareholderRightsPolicy: {
        status: shareholderRights === "yes",
        documentUrl,
        isRevised,
        statusremark: remark,
        effectiveDate,
      },
    };

    const quarterlyData = {
      shareholderMeetings: parseInt(stripNumberFormatting(shareholderMeetings), 10),
      shareholderVotingParticipationRate: {
        votesCast: parseInt(stripNumberFormatting(votesCast), 10),
        totalSharesOutstanding: parseInt(
          stripNumberFormatting(totalSharesOutstanding),
          10,
        ),
        performance: `${percentage}%`,
      },
    };

    const annualPayload = {
      id,
      module: "annual",
      shareholderRights: annualData,
      stepsCompleted: {
        shareholderRights: true,
      },
    };

    const quarterlyPayload = {
      id,
      module: "quarterly",
      shareholderRights: quarterlyData,
      stepsCompleted: {
        shareholderRights: true,
      },
    };

    const requestPayload = moduleType === "annual" ? annualPayload : quarterlyPayload;

    try {
      await put(`/governance/updateGovernance`, requestPayload)
        .then((response) => {
          navigate(`/governance/data/transparancy-disclosure/${id}`);
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    } catch (error) {
      console.error(
        "Error submitting compliance data:",
        error.response?.data || error.message
      );
    }
  };

  const handleTextChange = (e, field, setter, validator) => {
    const value = formatNumberInputWithCommas(e.target.value);
    const raw = stripNumberFormatting(value);

    // Set value and validate the field
    if (raw !== "" && !validator(value)) return;
    setter(value);

    // Validate and clear errors if valid
    if (validator(value)) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[field]; // Remove error for the specific field
        return newErrors;
      });
    }
  };

  const Back = async () => {
    if (moduleType === "annual") {
      navigate(`/governance/data/risk-management/${id}`);
    } else {
      navigate(`/governance/data/compliance-ethics/${id}`);
    }
  };

  // Section prefix: Annual uses "C", Quarterly uses "D"
  const sectionPrefix = moduleType === "annual" ? "C" : "D";

  // Same as GPolicyForm: only when not first annual year and previous-year policy has a document
  const shouldShowIsRevised =
    shareholderRights === "yes" &&
    !isFirstEntry &&
    hasUploadedDocument(previousPoliciesState?.documentUrl);

  const { min: effectiveDateMin, max: effectiveDateMax } =
    moduleType === "annual"
      ? getEffectiveDateMinMax(reportingYear)
      : { min: undefined, max: undefined };
  const isUploadDisabled =
    !(!isRevised&&!isFirstEntry && hasUploadedDocument(previousPoliciesState?.documentUrl));
    console.log("isRevised",isRevised)
  return (
    <MainDashboardLayout sidebar={<GovernanceLayout />}>
      <Box
        sx={{
          margin: "20px auto",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          minHeight: moduleType === "annual" ? "calc(100vh - 170px)" : "auto",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1976d2",
          },
        }}
      >
        <Box sx={{ flexGrow: moduleType === "annual" ? 1 : 0 }}>
         

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              margin: "1.2% 0",
              textDecoration: "underline",
            }}
          >
            {sectionPrefix}. SHAREHOLDER RIGHTS
          </Typography>

          {moduleType === "annual" && (
            <Box sx={{ margin: "2% 0 1% 0" }}>
              <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
                1. Existence of Shareholder Rights Policy.
              </Typography>
              <RadioGroup
                row
                sx={{ ml: "1.1rem" }}
                value={shareholderRights}
                onChange={(e) => handleShareholderRightsChange(e.target.value)}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                  label="No"
                />
              </RadioGroup>
              {shareholderRights === "yes" ? (
                <>
                  {shouldShowIsRevised && (
                    <Box sx={{ ml: "1.1rem", mt: 1 }}>
                      <Typography sx={{ fontWeight: "bold", mb: "4px" }}>
                        Is the Policy revised?
                      </Typography>
                      <RadioGroup
                        row
                        value={isRevised ? "yes" : "no"}
                        onChange={(e) =>
                          handleIsRevisedChange(e.target.value === "yes")
                        }
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                          label="No"
                        />
                      </RadioGroup>
                    </Box>
                  )}

                  <Box sx={{ ml: "0.9rem" }}>
                    <Typography sx={{ fontWeight: "bold", ml: "0.1rem", mb: 0.5 }}>
                      Upload evidence (for Policy):
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: hasUploadedDocument(documentUrl) ? "16px" : "40px",
                        ml: "0.1rem",
                      }}
                    >
                      <Box
                        sx={{
                          width: hasUploadedDocument(documentUrl) ? "233px" : "100px",
                        }}
                      >
                        <FileUpload
                          setDocumentUrl={handleFileChange}
                          filesList={documentUrl}
                          title={""}
                          ml={0}
                          mt={0}
                          marginTop={0}
                          isDisabled={isUploadDisabled}
                        />
                        {errors.documentUrl ? (
                          <FormHelperText
                            sx={{ color: "#D34040", fontSize: "14px", minHeight: "20px" }}
                          >
                            {errors.documentUrl}
                          </FormHelperText>
                        ) : null}
                      </Box>

                      <Box
                        sx={{
                          width: "30%",
                          marginTop: hasUploadedDocument(documentUrl) ? "15px" : "10px",
                        }}
                      >
                        <TextField
                          fullWidth
                          value={remark}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRemark(v);
                            if (String(v || "").trim()) {
                              setErrors((prevErrors) => {
                                const next = { ...prevErrors };
                                delete next.remark;
                                return next;
                              });
                            }
                          }}
                          placeholder="Enter Additional Text"
                          variant="outlined"
                          error={!!errors.remark}
                          multiline
                          minRows={1}
                          InputProps={{
                            sx: {
                              padding: "9px 12px",
                            },
                          }}
                        />
                        <FormHelperText
                          sx={{ color: "#D34040", fontSize: "14px", minHeight: "20px" }}
                        >
                          {errors.remark || " "}
                        </FormHelperText>
                      </Box>
                    </Box>

                    <Typography sx={{ fontWeight: "bold", ml: "0.1rem",mb:"12px" }}>
                      Effective date (for Policy):
                    </Typography>
                    <TextField
                      type="date"
                      fullWidth
                      value={effectiveDate || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEffectiveDate(v);
                        if (String(v || "").trim()) {
                          setErrors((prevErrors) => {
                            const next = { ...prevErrors };
                            delete next.effectiveDate;
                            return next;
                          });
                        }
                      }}
                      error={!!errors.effectiveDate}
                      // inputProps={{
                      //   ...(effectiveDateMin && effectiveDateMax
                      //     ? { min: effectiveDateMin, max: effectiveDateMax }
                      //     : {}),
                      // }}
                      inputProps={{
                        min: "2021-01-01",
                        ...(effectiveDateMax ? { max: effectiveDateMax } : {}),
                      }}
                      sx={{
                        marginBottom: "0.25rem",
                        width: "30%",
                        ml: "0.1rem",
                        "& .MuiInputBase-root": {
                          height: 40,
                        },
                      }}
                    />
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                        marginLeft: "0.1rem",
                        width: "30%",
                      }}
                    >
                      {errors.effectiveDate}
                    </FormHelperText>
                  </Box>
                </>
              ) : (
                <Box sx={{ ml: "1.1rem", mt: 1, width: "100%" }}>
                  <TextField
                    fullWidth
                    name="statusremark"
                    value={remark}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRemark(v);
                      if (String(v || "").trim()) {
                        setErrors((prevErrors) => {
                          const next = { ...prevErrors };
                          delete next.remark;
                          return next;
                        });
                      }
                    }}
                    placeholder="Enter Additional Text"
                    variant="outlined"
                    error={!!errors.remark}
                    multiline
                    minRows={1}
                    sx={{
                      width: "50%",
                    }}
                    InputProps={{
                      sx: {
                        padding: "9px 12px",
                      },
                    }}
                  />
                  <FormHelperText
                    sx={{ color: "#D34040", fontSize: "14px", minHeight: "20px" }}
                  >
                    {errors.remark || " "}
                  </FormHelperText>
                </Box>
              )}
            </Box>
          )}
        </Box>
        {moduleType !== "annual" && (
        <Box sx={{ margin: "2% 0 1% 0", width: "55%" }}>
          <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
            {moduleType === "annual"
              ? "2. Number of Shareholder Meetings"
              : "1. Number of Shareholder Meetings"}
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter Number"
            value={shareholderMeetings}
            error={!!errors.shareholderMeetings}
            onChange={(e) => {
              const next = e.target.value;
              if (next !== "" && !/^\d+$/.test(next)) return;
              handleTextChange(
                e,
                "shareholderMeetings",
                setShareholderMeetings,
                validateNumbers
              );
            }}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            variant="outlined"
            sx={{
              "& .MuiInputBase-root": {
                height: 40, // Reduce the text field height
              },
              ml:"1.1rem"
            }}
          />
          <FormHelperText sx={{ color: "#D34040", fontSize: "14px", marginLeft:"1.1rem" }}>
            {errors.shareholderMeetings}
          </FormHelperText>
        </Box>
        )}

        {moduleType !== "annual" && (
        <Box sx={{ margin: "2% 0 1% 0", width: "100%" }}>
          <Typography sx={{ fontWeight: "bold", marginBottom: "8px" }}>
            {moduleType === "annual"
              ? "3. Shareholder Voting Participation Rate:"
              : "2. Shareholder Voting Participation Rate"}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginTop: "8px",
              ml:"1.1rem"
            }}
          >
               <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontWeight: "bold" }}>
                Total Number Shares Outstanding
              </Typography>
              <TextField
                value={totalSharesOutstanding}
                placeholder="Enter Number"
                error={!!errors.totalSharesOutstanding}
                onChange={(e) => handlePercentageChange(e, "total")}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 40, // Reduce the text field height
                    width: "100%", // Adjust width
                  },
                }}
              />
              <FormHelperText sx={{ color: "#D34040", fontSize: "14px", minHeight: 20 }}>
                {errors.totalSharesOutstanding || " "}
              </FormHelperText>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px",  }}>
              <Typography sx={{ fontWeight: "bold" }}>
                No. of Votes Cast
              </Typography>
              <TextField
                value={votesCast}
                placeholder="Enter Number"
                error={!!errors.votesCast}
                onChange={(e) => handlePercentageChange(e, "trained")}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 40, // Reduce the text field height
                    width: "100%", // Adjust width
                  },
                }}
              />
              <FormHelperText sx={{ color: "#D34040", fontSize: "14px", minHeight: 20 }}>
                {errors.votesCast || " "}
              </FormHelperText>
            </Box>

         

            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" ,marginTop:"-24px"}}>
              <Typography sx={{ fontWeight: "bold", }}>
                Percentage
              </Typography>
              <TextField
                value={percentage ? `${percentage} %` : ""}
                placeholder="Percentage"
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    width: "100%",
                    border: "none",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
            </Box>


          </Box>
        </Box>
        )}

        <Box sx={{ mt: moduleType === "annual" ? "auto" : 0 }}>
          <ActionButtons Back={Back} Move={handleSubmit} />
        </Box>
      </Box>
    </MainDashboardLayout>
  );
};

export default ShareholderRights;
